const MIN_GROUP_SIZE = 10
const THIRD_PARTY_LABEL = "Sold by 3rd. party"

const LEFT_COLUMNS = new Set(["A", "B", "C"])
const RIGHT_COLUMNS = new Set(["D", "E", "F"])
const OUTLINE_PAD = 1
// Seat corner radius (MUI Paper, 4px) + OUTLINE_PAD, so outline corners stay concentric.
const OUTLINE_RADIUS = 5

export const LARGE_GROUP_COLORS = [
  "#E10600",
  "#111111",
  "#F5C400",
  "#00B3C7",
  "#7C3AED",
  "#F97316",
  "#DB2777",
  "#166534",
  "#9A3412",
  "#1D4ED8",
]

export const getPassengerGroup = (passenger) => {
  const bookingClass = String(passenger?.bookingClass || "").toUpperCase()

  if (bookingClass === "O") {
    return { key: "O", label: THIRD_PARTY_LABEL }
  }

  if (bookingClass !== "G") return null

  const name =
    String(passenger?.groupName || "").trim() ||
    String(passenger?.customerBookingReference || "").trim()

  return name ? { key: `G:${name}`, label: name } : null
}

export const buildLargeGroups = (passengerList = []) => {
  if (!Array.isArray(passengerList)) return []

  const groups = new Map()

  for (const passenger of passengerList) {
    if (passenger?.passengerType === "B" || passenger?.passengerType === "E") {
      continue
    }

    const group = getPassengerGroup(passenger)
    if (!group) continue

    const current = groups.get(group.key) || { ...group, count: 0 }
    current.count += 1
    groups.set(group.key, current)
  }

  return [...groups.values()]
    .filter(({ count }) => count >= MIN_GROUP_SIZE)
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const applyLargeGroupFilter = (passengers, selectedFilter, groupSource = passengers) => {
  if (!selectedFilter?.LARGE_GROUPS || !Array.isArray(passengers)) {
    return passengers
  }

  const largeGroupKeys = new Set(buildLargeGroups(groupSource).map(({ key }) => key))
  const selectedGroup = largeGroupKeys.has(selectedFilter.selectedGroup)
    ? selectedFilter.selectedGroup
    : null

  return passengers.filter((passenger) => {
    if (passenger?.passengerType === "B" || passenger?.passengerType === "E") {
      return false
    }

    const group = getPassengerGroup(passenger)
    return group && largeGroupKeys.has(group.key) && (!selectedGroup || group.key === selectedGroup)
  })
}

export const getGroupColor = (groupKey, groups = []) => {
  const index = groups.findIndex(({ key }) => key === groupKey)
  if (index < 0) return LARGE_GROUP_COLORS[0]
  return LARGE_GROUP_COLORS[index % LARGE_GROUP_COLORS.length]
}

export const aisleSide = (column) => {
  const col = String(column || "").toUpperCase()
  if (LEFT_COLUMNS.has(col)) return "L"
  if (RIGHT_COLUMNS.has(col)) return "R"
  return null
}

export const seatId = (row, column) => `${row}${String(column || "").toUpperCase()}`

// Buckets near-equal box starts into ordered bands (rendered rows or columns).
const toBands = (items, start, end) => {
  const tolerance = Math.min(...items.map((item) => end(item) - start(item))) / 2
  const bands = []
  const index = new Map()

  for (const item of [...items].sort((a, b) => start(a) - start(b))) {
    let band = bands[bands.length - 1]
    if (!band || start(item) - band.min > tolerance) {
      band = { min: start(item), max: end(item) }
      bands.push(band)
    }
    band.max = Math.max(band.max, end(item))
    index.set(item, bands.length - 1)
  }

  return { bands, index }
}

const toPath = (points) => {
  const rounded = points.map(([x, y]) => [Math.round(x), Math.round(y)])
  const distinct = rounded.filter(([x, y], i) => {
    const [nx, ny] = rounded[(i + 1) % rounded.length]
    return x !== nx || y !== ny
  })
  const corners = distinct.filter(([x, y], i) => {
    const [px, py] = distinct[(i - 1 + distinct.length) % distinct.length]
    const [nx, ny] = distinct[(i + 1) % distinct.length]
    return !(px === x && x === nx) && !(py === y && y === ny)
  })

  // Every corner becomes a circular arc, shrunk on short edges so neighbours never overlap.
  const span = (a, b) => Math.abs(b[0] - a[0]) + Math.abs(b[1] - a[1])
  const toward = (from, to, r) => from.map((v, axis) => v + ((to[axis] - v) / span(from, to)) * r)
  return `${corners
    .map((corner, i) => {
      const prev = corners[(i - 1 + corners.length) % corners.length]
      const next = corners[(i + 1) % corners.length]
      const r = Math.min(OUTLINE_RADIUS, span(prev, corner) / 2, span(corner, next) / 2)
      const turn =
        (corner[0] - prev[0]) * (next[1] - corner[1]) -
        (corner[1] - prev[1]) * (next[0] - corner[0])
      const from = toward(corner, prev, r).join(" ")
      const to = toward(corner, next, r).join(" ")
      return `${i ? "L" : "M"} ${from} A ${r} ${r} 0 0 ${turn > 0 ? 1 : 0} ${to}`
    })
    .join(" ")} Z`
}

// Outlines every large group on the rendered seat map. `seats` are the measured boxes of ALL
// rendered seats ({ column, groupKey, left, top, width, height }), groupKey null for anyone
// outside a large group. Geometry comes from the boxes, not seat letters, so short rows and
// rotation just work. Two members join only when they are direct neighbours: same row and
// aisle side, or the next rendered row in the same column (bridging zone/exit gaps). Empty
// seats and other passengers are never covered; far-away members get their own sub-path.
export const groupOutlinePaths = (seats = [], pad = OUTLINE_PAD) => {
  if (!seats.length) return []

  const rows = toBands(
    seats,
    (seat) => seat.top,
    (seat) => seat.top + seat.height,
  )
  const cols = toBands(
    seats,
    (seat) => seat.left,
    (seat) => seat.left + seat.width,
  )
  const grid = rows.bands.map(() => [])
  const sides = []
  for (const seat of seats) {
    const c = cols.index.get(seat)
    grid[rows.index.get(seat)][c] = seat.groupKey
    if (sides[c] == null) sides[c] = aisleSide(seat.column)
  }
  const sameSide = (c) => sides[c] != null && sides[c] === sides[c + 1]

  // Doubled grid: even index = seat band, odd index = gap to the next band.
  const xs = cols.bands.flatMap(({ min, max }) => [min - pad, max + pad])
  const ys = rows.bands.flatMap(({ min, max }) => [min - pad, max + pad])
  const groupKeys = new Set(seats.map(({ groupKey }) => groupKey).filter(Boolean))

  return [...groupKeys].map((groupKey) => {
    const member = (r, c) => grid[r]?.[c] === groupKey
    const filled = (i, j) => {
      if (i < 0 || j < 0) return false
      const c = Math.floor(i / 2)
      const r = Math.floor(j / 2)
      const rowGap = j % 2 === 1
      const colGap = i % 2 === 1
      if (colGap && !(sameSide(c) && member(r, c) && member(r, c + 1))) return false
      if (rowGap && !(member(r, c) && member(r + 1, c))) return false
      if (colGap && rowGap) return member(r + 1, c + 1)
      return member(r, c)
    }

    // Clockwise boundary edges between filled and empty cells, keyed by start vertex.
    // The fill rules never let two cells touch only at a corner, so every vertex has at most
    // one outgoing edge and loops trace unambiguously.
    const next = new Map()
    const edge = (k1, l1, k2, l2) => next.set(`${k1},${l1}`, [k2, l2])
    for (let j = 0; j < ys.length - 1; j++) {
      for (let i = 0; i < xs.length - 1; i++) {
        if (!filled(i, j)) continue
        if (!filled(i, j - 1)) edge(i, j, i + 1, j)
        if (!filled(i + 1, j)) edge(i + 1, j, i + 1, j + 1)
        if (!filled(i, j + 1)) edge(i + 1, j + 1, i, j + 1)
        if (!filled(i - 1, j)) edge(i, j + 1, i, j)
      }
    }

    const loops = []
    for (const start of next.keys()) {
      const loop = []
      let key = start
      while (next.has(key)) {
        const [k, l] = next.get(key)
        next.delete(key)
        loop.push([xs[k], ys[l]])
        key = `${k},${l}`
      }
      loops.push(toPath(loop))
    }

    return { groupKey, d: loops.join(" ") }
  })
}
